import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import classNames from 'classnames';
import { makeStyles, Typography } from '@material-ui/core';
import { stripHtml } from 'string-strip-html';
import { N_A, BOX_SIZE_BREAKPOINT } from './config';
import RichTextPreview from '../richTextPreview';
import TextBoxWithTooltip from '../textBoxWithTooltip';
import InputField from '../inputField';
import ActionButton from '../actionButton';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  questionWrapper: {
    marginBottom: spacing(6),
  },
  headerSection: {
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  titleWrapper: {
    marginTop: spacing(7.75),
    marginBottom: spacing(3.25),
    justifyContent: 'space-between',
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
  },
  label: {
    lineHeight: '24px',
  },
  answersWrapper: {
    padding: spacing(1, 0, 4, 0),
  },
  horizontalAnswerWeight: {
    justifyContent: 'center',
    display: 'flex',
  },
  verticalAnswerWeight: {
    marginBottom: spacing(2),
    marginLeft: spacing(3),
  },
  verticalAnswerWrapper: {
    display: 'flex',
    alignItems: 'center',
    '& .MuiFormControl-root': {
      width: '91.5%',
    },
  },
  verticalAnswerRichTextWrapper: {
    justifyContent: 'space-between',
  },
  weightBlock: {
    display: 'flex',
  },
  horizontalWeightLabel: {
    marginTop: spacing(13.5),
  },
  horizontalAnswers: {
    display: 'flex',
    justifyContent: 'flex-start',
  },
  horizontalAnswerWrapper: {
    marginRight: spacing(2),
    display: 'grid',
    justifyContent: 'flex-start',
    '&:last-child': {
      marginRight: 0,
    },
  },
  horizontalMediumBox: {
    width: 78.5,
  },
  horizontalLargeBox: {
    width: 107,
  },
  horizontalSmallBox: {
    width: 'auto',
  },
  inputHorizontalStyle: {
    backgroundColor: primary.white,
    marginBottom: spacing(2),
    padding: spacing(0, 0.5),
  },
  inputHorizontalAnswers: {
    width: '100%',
    textAlign: 'center',
  },
  mainRichText: {
    width: '100%',
  },
  richTextEditorWrapper: {
    width: '91.5%',
  },
  richText: {
    padding: spacing(0, 0, 4, 0),
    width: '91.5%',
  },
  richTextBorder: {
    border: `1px solid ${primary.bluish6}`,
    padding: spacing(4),
    boxSizing: 'border-box',
    borderRadius: spacing(2),
    width: '91.5%',
  },
  disabled: {
    opacity: '30%',
  },
  questionLabel: {
    color: primary.bluish3,
  },
}));

const ArchivedQuestion = ({
  translations,
  item,
  initialItem = null,
  answerWeights,
  isVertical,
  hasDefinedAnswers = false,
  isEditDisabled,
  handleUnarchive,
}) => {
  const classes = useStyles();
  const numberOfAnswers = item.answers.length;

  const isDisabledUnarchive =
    isEditDisabled &&
    initialItem &&
    item.is_used_in_survey &&
    initialItem.is_archived;

  return (
    <div className={classes.questionWrapper}>
      <Grid
        container
        spacing={0}
        className={classNames(classes.titleWrapper, classes.headerSection)}
      >
        <Grid item xs={11} className={classes.leftSection}>
          <TextBoxWithTooltip
            className={classes.questionLabel}
            variant="h5"
            text={item?.text}
          />
        </Grid>
        <ActionButton
          className={classNames({
            [classes.disabled]: isDisabledUnarchive,
          })}
          type={ACTION_BUTTON_TYPES.UNARCHIVE}
          isDisabled={isDisabledUnarchive}
          disabledTooltipText={translations.unarchivedDisabledTooltipText}
          onClickHandler={() => handleUnarchive(item)}
        />
      </Grid>
      {hasDefinedAnswers && (
        <div>
          <div className={classes.headerSection}>
            <Typography variant="caption" className={classes.label}>
              {translations.answers}
            </Typography>
            {isVertical && (
              <Typography variant="caption" className={classes.label}>
                {translations.weight}
              </Typography>
            )}
          </div>
          <div className={classes.weightBlock}>
            <Grid
              item
              xs={12}
              className={classNames(classes.answersWrapper, {
                [classes.horizontalAnswers]: !isVertical,
              })}
            >
              {item?.answers.map((answer, index) => {
                const isNeutralAnswer = +answerWeights[index] < 0;
                const inputValue = isNeutralAnswer ? N_A : answer.text;
                const weightValue = isNeutralAnswer
                  ? ''
                  : `${answerWeights[index]}`;

                return (
                  <div
                    key={`answer_${index}`}
                    className={classNames({
                      [classes.horizontalAnswerWrapper]: !isVertical,
                      [classes.verticalAnswerWrapper]:
                        isVertical && !isNeutralAnswer,
                      [classes.verticalAnswerRichTextWrapper]: isVertical,
                      [classes.horizontalMediumBox]:
                        numberOfAnswers <
                          BOX_SIZE_BREAKPOINT.THRESHOLD_FOR_LARGE &&
                        !isVertical,
                      [classes.horizontalLargeBox]:
                        numberOfAnswers <
                          BOX_SIZE_BREAKPOINT.THRESHOLD_FOR_MEDIUM &&
                        !isVertical,
                      [classes.horizontalSmallBox]:
                        numberOfAnswers >=
                          BOX_SIZE_BREAKPOINT.THRESHOLD_FOR_LARGE &&
                        !isVertical,
                    })}
                  >
                    {isVertical && (
                      <div
                        className={classNames({
                          [classes.richText]: isVertical,
                        })}
                      >
                        <RichTextPreview
                          className={classNames(
                            classes.label,
                            classes.questionLabel
                          )}
                          variant="body1"
                          text={inputValue}
                        />
                      </div>
                    )}
                    {!isVertical && (
                      <InputField
                        className={classes.inputHorizontalStyle}
                        customInputClass={classNames(
                          classes.inputHorizontalAnswers,
                          classes.questionLabel
                        )}
                        readOnly
                        value={stripHtml(inputValue).result}
                      />
                    )}
                    <Typography
                      variant="subtitle1"
                      className={classNames(classes.label, {
                        [classes.horizontalAnswerWeight]: !isVertical,
                      })}
                    >
                      {weightValue}
                    </Typography>
                  </div>
                );
              })}
            </Grid>
            {!isVertical && (
              <Typography
                variant="caption"
                className={classNames(
                  classes.label,
                  classes.horizontalWeightLabel
                )}
              >
                {translations.weight}
              </Typography>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

ArchivedQuestion.propTypes = {
  item: PropTypes.object.isRequired,
  translations: PropTypes.object.isRequired,
  initialItem: PropTypes.object,
  answerWeights: PropTypes.array.isRequired,
  isVertical: PropTypes.bool.isRequired,
  hasDefinedAnswers: PropTypes.bool,
  isEditDisabled: PropTypes.bool.isRequired,
  handleUnarchive: PropTypes.func.isRequired,
};

export default ArchivedQuestion;
